import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, DashboardModule, Link,
} from '@domain-group/suit-theme'

import { useFilters } from '@domain-group/react-hooks'

import { generateRedirectUrl, sortArrayBy } from '@domain-group/javascript-utils'

import useAgencyProvider from '@hooks/useAgencyProvider'
import useAgencyProviders from '@hooks/useAgencyProviders'
import { generateUrls } from '@hooks/useProviderPriceList'
import useServices from '@hooks/useServices'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import type { ServiceModel } from '@models/types'
import ProviderPriceListItem from './ProviderPriceListItem'

const buildPageFilterFields = (filteredServices: ServiceModel[]) => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'price_list_string',
      label: 'Filter by Title or ID',
    },
    {
      component: 'DynamicInput',
      componentProps: {
        defaultSelectLabel: 'All',
        inputComponent: 'select',
        style,
      },
      key: 'service_id',
      label: 'Service',
      values: sortArrayBy(Object.values(filteredServices), 'asc', 'title').map(service => ({
        key: service.id,
        label: service.title,
      })),
    },
  ]
}

const ProviderPriceLists = (props: { agencyId: number, providerId: number}) => {
  const { agencyId, providerId } = props

  const filterPayload = useFilters()
  const {
    pageFilters,
  } = filterPayload

  const options = {
    filters: { ...pageFilters, agency_id: agencyId, provider_id: providerId },
    performHttpRequests: !!providerId && !!agencyId,
    requestOptions: {
      include_agency_provider_provider_price_list: true,
      include_provider_price_list_service: true,
    },
  }
  const providerPricesListsPayload = useAgencyProviders(options)

  const { filteredServices } = useServices({ performHttpRequests: true })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAgencyProviders,
    filteredAgencyProvidersCount,
    hasFilteredAgencyProviders,
    loading,
  } = providerPricesListsPayload

  const { providerPriceListsIndexUrl } = generateUrls()

  const agencyProviderPayload = useAgencyProvider({ provider_id: providerId, agency_id: agencyId })
  const { callbacks: { createOrEditAgencyProvider } } = agencyProviderPayload

  return (
    <DashboardModule
      boxProps={{ id: 'realhub-nav-price-lists' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title="Price Lists"
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields(filteredServices)}
        {...filterPayload}
      />

      <Box flexDirection="column">
        {!loading && !hasFilteredAgencyProviders && (
          <DashboardModule.BlankState>
            <DashboardModule.BlankState.Title>
              No Price Lists present
            </DashboardModule.BlankState.Title>

            <DashboardModule.BlankState.Paragraph>
              You can create a price list
              <Link href={`${providerPriceListsIndexUrl}?redirect=${generateRedirectUrl()}`}> here</Link>.
            </DashboardModule.BlankState.Paragraph>
          </DashboardModule.BlankState>
        )}

        {!loading && hasFilteredAgencyProviders && (
          // @ts-expect-error TS2322
          <DashboardModule.ScrollableSection flexDirection="column">
            {filteredAgencyProviders.map(agencyProvider => (
              <ProviderPriceListItem
                key={agencyProvider.id}
                agencyProvider={agencyProvider}
              />
            ))}
          </DashboardModule.ScrollableSection>
        )}

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredAgencyProvidersCount}
          loading={loading}
        >
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => createOrEditAgencyProvider()}
            // @ts-expect-error TS2322
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            Add Price List
          </Button>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}
export default ProviderPriceLists
